/**
 * Overdue Aging Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows overdue invoices with aging bucket summary cards (1-30, 31-60, 61-90, 90+)
 */
import { useOverdueAging } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateOnly } from '@/lib/utils';

type SortBy = 'days' | 'amount' | 'client';

export function OverdueAging() {
  const { data, isLoading, error } = useOverdueAging();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>('days');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load overdue aging data
      </div>
    );
  }

  const invoices = data?.data || [];
  const buckets = data?.buckets || {
    '1-30': 0,
    '31-60': 0,
    '61-90': 0,
    '90+': 0,
  };
  const summary = data?.summary || {
    total_overdue: 0,
    invoice_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    // due_date is a DATE column. Parsing it with the bare Date constructor
    // treats it as UTC midnight, which renders one calendar day early west
    // of UTC (e.g. America/Edmonton). formatDateOnly parses the y/m/d
    // components directly so the displayed date always matches the stored
    // calendar date.
    return formatDateOnly(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Sort invoices
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortBy === 'days') {
      return b.days_overdue - a.days_overdue;
    } else if (sortBy === 'amount') {
      return b.total_amount - a.total_amount;
    } else {
      return (a.client_name || '').localeCompare(b.client_name || '');
    }
  });

  const handleRowClick = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const getBucketColor = (bucket: string) => {
    switch (bucket) {
      case '1-30':
        return 'bg-yellow-100 text-yellow-800';
      case '31-60':
        return 'bg-orange-100 text-orange-800';
      case '61-90':
        return 'bg-red-100 text-red-800';
      case '90+':
        return 'bg-red-200 text-red-900';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="overdue-aging">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Overdue Aging
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('days')}
            className={`px-3 py-1 rounded ${sortBy === 'days' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Days Overdue
          </button>
          <button
            onClick={() => setSortBy('amount')}
            className={`px-3 py-1 rounded ${sortBy === 'amount' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Amount
          </button>
          <button
            onClick={() => setSortBy('client')}
            className={`px-3 py-1 rounded ${sortBy === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Client
          </button>
        </div>
      </div>

      {/* Summary Card - Total */}
      <div className="bg-red-50 p-4 rounded-lg mb-4">
        <div className="text-sm text-gray-600">Total Overdue</div>
        <div className="text-2xl font-bold text-red-600" data-testid="summary-total">
          {formatCurrency(summary.total_overdue)}
        </div>
        <div className="text-sm text-gray-500">{summary.invoice_count} invoices</div>
      </div>

      {/* Aging Bucket Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="text-sm text-gray-600">1-30 Days</div>
          <div className="text-xl font-bold text-yellow-700" data-testid="bucket-1-30">
            {formatCurrency(buckets['1-30'])}
          </div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="text-sm text-gray-600">31-60 Days</div>
          <div className="text-xl font-bold text-orange-700" data-testid="bucket-31-60">
            {formatCurrency(buckets['31-60'])}
          </div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-sm text-gray-600">61-90 Days</div>
          <div className="text-xl font-bold text-red-600" data-testid="bucket-61-90">
            {formatCurrency(buckets['61-90'])}
          </div>
        </div>
        <div className="bg-red-100 p-4 rounded-lg border border-red-300">
          <div className="text-sm text-gray-600">90+ Days</div>
          <div className="text-xl font-bold text-red-800" data-testid="bucket-90+">
            {formatCurrency(buckets['90+'])}
          </div>
        </div>
      </div>

      {/* Table */}
      {sortedInvoices.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Overdue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInvoices.map((invoice) => (
                <tr
                  key={invoice.invoice_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleRowClick(invoice.invoice_id)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {invoice.invoice_number}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="text-gray-600">
                      {invoice.project_number ? `#${invoice.project_number}` : 'Proposal'}
                    </span>
                    <span className="ml-2 text-gray-500">{invoice.project_name}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {invoice.client_name || 'No Client'}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-red-600">
                    {formatCurrency(invoice.total_amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(invoice.due_date)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBucketColor(invoice.aging_bucket)}`}>
                      {invoice.days_overdue} days
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No overdue invoices - great job!
        </div>
      )}
    </div>
  );
}
